'use client';

import { useState } from 'react';
import { toast } from 'react-toastify';
import { Box } from '@mui/material';

import type { EventResponse } from '@/types';
import { formatVolume, handleApiError } from '@/utils';
import { DesktopMarketHeader } from './DesktopMarketHeader';
import { MarketAlertDialog } from './MarketAlertDialog';
import { MobileMarketHeader } from './MobileMarketHeader';

import { MESSAGES } from '@/constants';

interface MarketHeaderProps {
  event: EventResponse | null;
  totalVolume: number;
  isBookmarked: boolean;
  onBookmarkClick: () => void;
}

export function MarketHeader({
  event,
  totalVolume,
  isBookmarked,
  onBookmarkClick,
}: MarketHeaderProps) {
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const [alertOpen, setAlertOpen] = useState(false);

  const handleShareClick = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      toast.success(MESSAGES.GENERAL.LINK_COPIED);
    } catch (err) {
      handleApiError(err);
    }
  };

  const mainMarket = event?.markets?.[0];

  const formattedTotalVolume = formatVolume(totalVolume);

  const resolutionDate = new Date(
    mainMarket?.endDateIso || mainMarket?.endDate || '',
  ).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <MobileMarketHeader
        event={event}
        formattedTotalVolume={formattedTotalVolume}
        isBookmarked={isBookmarked}
        onBookmarkClick={onBookmarkClick}
        onShareClick={handleShareClick}
        onAlertClick={() => setAlertOpen(true)}
      />

      <DesktopMarketHeader
        event={event}
        formattedTotalVolume={formattedTotalVolume}
        resolutionDate={resolutionDate}
        isBookmarked={isBookmarked}
        onBookmarkClick={onBookmarkClick}
        onShareClick={handleShareClick}
        onAlertClick={() => setAlertOpen(true)}
      />

      <MarketAlertDialog open={alertOpen} onClose={() => setAlertOpen(false)} event={event} />
    </Box>
  );
}
